import styled from "styled-components";

import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { Banner, BannerTone } from "@pages/AdminDashboard/ui/Banner";
import { UiKitSection } from "@pages/AdminDashboard/ui/UiKitSection";

interface ToneExample {
  label: string;
  tone: BannerTone;
  title: string;
  body: string;
  actionLabel: string;
}

// One short example per tone, worded from the design's Redo and Ship banners
// with the design's fake data (BANNER-R11).
const toneExamples: ToneExample[] = [
  {
    label: "Success",
    tone: "success",
    title: "Replaced. OID e5f1-test-0003 was sent to the pharmacy.",
    body: "Paid by the original invoice; the User was not charged.",
    actionLabel: "View Order",
  },
  {
    label: "Info",
    tone: "info",
    title: "Already replaced. No new Order was created.",
    body: "OID b72e-test-0002 was already superseded by OID e5f1-test-0003. Check its status before doing anything else.",
    actionLabel: "Select Order",
  },
  {
    label: "Warning",
    tone: "warning",
    title: "OID e5f1-test-0003 was created but not sent to the pharmacy.",
    body: "It is paid by the original invoice. Re-send it from here; replacing again is not needed.",
    actionLabel: "Re-send",
  },
  {
    label: "Error",
    tone: "error",
    title: "Replace didn’t go through. Nothing changed.",
    body: "OID b72e-test-0002 is still the active Order.",
    actionLabel: "Try again",
  },
];

const longWord = "b72e-test-0002-".repeat(8);
const longTitle = `Can’t cancel: OID ${longWord} has already shipped.`;
const longBody = `A shipped Order is never recalled. To send the right medication or ship to a corrected address, replace it: the shipped Order stays as is and a corrective Order ships under the same payment. Reference ${longWord}.`;

const [spaceXs, , spaceMd, , spaceXl] = adminTokens.spacing;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(480px, 100%), 1fr));
  gap: ${spaceMd}px;
  margin-bottom: ${spaceXl}px;
`;

const Example = styled.div.attrs({ role: "group" })`
  display: flex;
  flex-direction: column;
  gap: ${spaceXs}px;
  min-width: 0;
`;

const Caption = styled.span`
  color: ${adminTokens.color.inkSoft};
  font-family: ${adminTokens.typeScale.meta.fontFamily};
  font-size: ${adminTokens.typeScale.meta.fontSize};
`;

const noAction = () => undefined;

export const BannerSection = () => (
  <UiKitSection title="Banner">
    <Grid>
      {toneExamples.map(({ label, tone, title, body }) => (
        <Example key={`${label}-none`} aria-label={`${label}, no action`}>
          <Caption>{`${label}, no action`}</Caption>
          <Banner tone={tone} title={title} body={body} />
        </Example>
      ))}
      {toneExamples.map(({ label, tone, title, body, actionLabel }) => (
        <Example key={`${label}-action`} aria-label={`${label}, with action`}>
          <Caption>{`${label}, with action`}</Caption>
          <Banner
            tone={tone}
            title={title}
            body={body}
            action={{ label: actionLabel, onAction: noAction }}
          />
        </Example>
      ))}
      <Example aria-label="Action pending">
        <Caption>Action pending</Caption>
        <Banner
          tone="warning"
          title={toneExamples[2].title}
          body={toneExamples[2].body}
          action={{
            label: "Re-send",
            onAction: noAction,
            isPending: true,
            pendingLabel: "Re-sending…",
          }}
        />
      </Example>
      <Example aria-label="Long title and body">
        <Caption>Long title and body</Caption>
        <Banner
          tone="error"
          title={longTitle}
          body={longBody}
          action={{ label: "Replace Order (reship)", onAction: noAction }}
        />
      </Example>
    </Grid>
  </UiKitSection>
);
